import type { Sort } from 'app/common/types';
import { type BoundActionType, bindAction, createBoundAction } from 'app/common/utils/actions';
import { createBoundRoutine } from 'app/common/utils/new-routines';
import type { Dispatch } from 'redux';
import type { SearchFilter, SearchParams, SearchResponse, TaxonomySuggestResponse } from './types';

export type SearchMeta = {
  searchName: string;
};

export const search = createBoundRoutine('contentSearch/SEARCH')<
  SearchMeta,
  Partial<SearchParams>,
  undefined,
  SearchResponse,
  string,
  undefined
>();

export const suggestTaxonomy = createBoundRoutine('contentSearch/SUGGEST_TAXONOMY')<
  SearchMeta,
  undefined,
  undefined,
  TaxonomySuggestResponse,
  string,
  undefined
>();

export const updateQuery = createBoundAction('contentSearch/UPDATE_QUERY')<SearchMeta, string>();
export const updateSort = createBoundAction('contentSearch/UPDATE_SORT')<SearchMeta, Sort>();
export const clearSearch = createBoundAction('contentSearch/CLEAR_SEARCH')<SearchMeta>();
export const paginateForward = createBoundAction('contentSearch/PAGINATE_FORWARD')<SearchMeta>();
export const paginateBack = createBoundAction('contentSearch/PAGINATE_BACK')<SearchMeta>();
export const filterResults = createBoundAction('contentSearch/FILTER_RESULTS')<SearchMeta>();
export const updateShapeIds = createBoundAction('contentSearch/UPDATE_CONTENT_TYPES')<SearchMeta, string[]>();
export const updateFilter = createBoundAction('contentSearch/UPDATE_FILTER')<
  SearchMeta,
  { selection: SearchFilter[]; clearQuery?: boolean }
>();
export const initContentSearch = createBoundAction('contentSearch/INIT_CONTENT_SEARCH')<SearchMeta>();

export type Actions =
  | BoundActionType<typeof search.requested>
  | BoundActionType<typeof search.pending>
  | BoundActionType<typeof search.fulfilled>
  | BoundActionType<typeof search.rejected>
  | BoundActionType<typeof search.cancelled>
  | BoundActionType<typeof suggestTaxonomy.requested>
  | BoundActionType<typeof suggestTaxonomy.pending>
  | BoundActionType<typeof suggestTaxonomy.fulfilled>
  | BoundActionType<typeof suggestTaxonomy.rejected>
  | BoundActionType<typeof suggestTaxonomy.cancelled>
  | BoundActionType<typeof updateQuery>
  | BoundActionType<typeof updateSort>
  | BoundActionType<typeof clearSearch>
  | BoundActionType<typeof paginateForward>
  | BoundActionType<typeof paginateBack>
  | BoundActionType<typeof filterResults>
  | BoundActionType<typeof updateShapeIds>
  | BoundActionType<typeof updateFilter>
  | BoundActionType<typeof initContentSearch>;

export type SearchActions = {
  search: ReturnType<typeof search.requested>;
  suggestTaxonomy: ReturnType<typeof suggestTaxonomy.requested>;
  updateQuery: ReturnType<typeof updateQuery>;
  updateSort: ReturnType<typeof updateSort>;
  clearSearch: ReturnType<typeof clearSearch>;
  paginateForward: ReturnType<typeof paginateForward>;
  paginateBack: ReturnType<typeof paginateBack>;
  filterResults: ReturnType<typeof filterResults>;
  updateShapeIds: ReturnType<typeof updateShapeIds>;
  updateFilter: ReturnType<typeof updateFilter>;
  initContentSearch: ReturnType<typeof initContentSearch>;
};

export function bindSearchActions(searchName: string, dispatch?: Dispatch): SearchActions {
  const meta: SearchMeta = { searchName };
  return {
    search: bindAction(search.requested, meta, dispatch),
    suggestTaxonomy: bindAction(suggestTaxonomy.requested, meta, dispatch),
    updateQuery: bindAction(updateQuery, meta, dispatch),
    updateSort: bindAction(updateSort, meta, dispatch),
    clearSearch: bindAction(clearSearch, meta, dispatch),
    paginateForward: bindAction(paginateForward, meta, dispatch),
    paginateBack: bindAction(paginateBack, meta, dispatch),
    filterResults: bindAction(filterResults, meta, dispatch),
    updateShapeIds: bindAction(updateShapeIds, meta, dispatch),
    updateFilter: bindAction(updateFilter, meta, dispatch),
    initContentSearch: bindAction(initContentSearch, meta, dispatch)
  };
}
